import { CertificateActivationTriggerEnum } from "./certificate.interface.js";

/**
 * VRG-528 — the ONE function that answers "when does this certificate activate".
 *
 * ## Why this lives in `packages/types` and not in `apps/api`
 *
 * Activation is the metered, billed event, so the answer determines when a
 * customer is charged. The platform ALSO has to render that answer — a product
 * page and a certificate page each show the resolved trigger and where it came
 * from. If the server resolved the chain one way and the UI re-implemented it,
 * the UI could tell a brand "activates on sale" while the server billed at
 * creation, and nothing would fail: both sides would be internally consistent
 * and quietly disagree. A shared pure function is the only shape where that
 * divergence is impossible rather than merely unlikely.
 *
 * So: no imports beyond the enum, no I/O, no framework. Both apps call THIS.
 *
 * ## The chain
 *
 * Most specific wins; `null` at any level means "inherit from the level above".
 *
 *   certificate.activation_trigger
 *     ?? product.activation_trigger
 *     ?? certificate_trigger_settings.activation_trigger   (company)
 *     ?? ON_PRIMARY_TRANSFER                               (system default)
 *
 * `null` is never "unconfigured pending a default" — it is an active decision to
 * follow the level above. This is why neither override column carries a DB
 * default and why neither may ever be backfilled: writing today's company value
 * onto every row would sever inheritance permanently and invisibly.
 */

/**
 * The trigger as a VALUE, in either form.
 *
 * `apps/api` holds it as the TS enum; `apps/platform` holds it as the string union
 * that arrives over the wire (its `CertificateActivationTrigger`). Those are the
 * same three strings at runtime but are NOT mutually assignable in TypeScript, so a
 * resolver typed only to the enum forces every client call site to cast — and a
 * cast at the boundary is exactly where a fourth trigger added later would slip
 * through unchecked. Accepting both keeps the call sites honest while the RETURN
 * stays the enum, so server-side comparisons remain exhaustive.
 */
export type ActivationTriggerValue =
  | CertificateActivationTriggerEnum
  | `${CertificateActivationTriggerEnum}`;

/** Which level of the chain supplied the resolved value. */
export enum ActivationTriggerSource {
  /** Set on this individual certificate — the most specific override. */
  Certificate = "certificate",
  /** Set on the product (SKU); this certificate inherits it. */
  Product = "product",
  /** The company's Settings -> Features default. */
  Company = "company",
  /** Nothing was set anywhere; the system default applied. */
  Default = "default",
}

export interface ResolvedActivationTrigger {
  trigger: CertificateActivationTriggerEnum;
  source: ActivationTriggerSource;
}

/**
 * The system default when no level sets anything.
 *
 * ON_PRIMARY_TRANSFER, not ON_CREATION, and the choice is deliberately the
 * customer-favourable one: it defers the billable moment to the sale. A company
 * that has never opened the Features page should not be billed at creation
 * because of a default it never chose.
 */
export const DEFAULT_ACTIVATION_TRIGGER =
  CertificateActivationTriggerEnum.ON_PRIMARY_TRANSFER;

/**
 * Resolve the activation trigger for one certificate.
 *
 * Every argument is optional and nullable because callers legitimately know
 * different amounts: a product page has no certificate level, and a creation
 * path may not have persisted the certificate yet. An omitted level is treated
 * exactly like an explicit `null` — inherit — which keeps "I don't have this
 * level" and "this level defers" from needing different handling at call sites.
 */
export function resolveActivationTrigger(levels: {
  certificate?: ActivationTriggerValue | null;
  product?: ActivationTriggerValue | null;
  company?: ActivationTriggerValue | null;
}): ResolvedActivationTrigger {
  // The enum's members ARE these strings, so widening the input and narrowing the
  // output here is a re-label, not a conversion. It happens once, in this function,
  // rather than at every call site.
  const asEnum = (value: ActivationTriggerValue) =>
    value as CertificateActivationTriggerEnum;

  if (levels.certificate != null) {
    return {
      trigger: asEnum(levels.certificate),
      source: ActivationTriggerSource.Certificate,
    };
  }
  if (levels.product != null) {
    return {
      trigger: asEnum(levels.product),
      source: ActivationTriggerSource.Product,
    };
  }
  if (levels.company != null) {
    return {
      trigger: asEnum(levels.company),
      source: ActivationTriggerSource.Company,
    };
  }
  return {
    trigger: DEFAULT_ACTIVATION_TRIGGER,
    source: ActivationTriggerSource.Default,
  };
}
